import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import { LeadtimeType } from '@utils/types'
import FormBlurSaveInput from '../FormBlurSaveInput/FormBlurSaveInput'


const normalizeDisplayValue = (value: number | string) => {
  if (typeof value === 'number') {
    if (value >= 999) return 999
    return value
  }
  const trimmed = value.trim()
  if (!trimmed) return ''
  if (trimmed.toLowerCase().includes('richiest')) return 999
  const parsed = parseInt(trimmed, 10)
  if (Number.isNaN(parsed)) return trimmed
  return parsed >= 999 ? 999 : parsed
}

const resolveInitialValue = (quantita: number, leadtime: LeadtimeType) => {
  const cellValue = leadtime?.values?.[quantita]
  if (cellValue === undefined || cellValue === null) return ''
  const normalized = normalizeDisplayValue(cellValue)
  return normalized.toString()
}

const normalizeValue = (rawValue: string): number | null | undefined => {
  if (rawValue === undefined || rawValue === null)
    return null
  const trimmed = rawValue.toString().trim()
  if (trimmed === '')
    return null
  if (trimmed.toLowerCase().includes('richiest'))
    return 999
  const parsed = parseInt(trimmed, 10)
  if (Number.isNaN(parsed))
    return undefined
  return parsed
}


type InputStatus = 'default' | 'success' | 'error'

export type FormLeadtimeInputProps = {
  leadtime?: LeadtimeType
  quantita?: number
  disabled?: boolean
  className?: string
  onSave?: (value: number | null) => Promise<void> | void
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>

const FormLeadtimeInput = ({
  leadtime,
  quantita,
  disabled = false,
  className,
  onSave = async () => { },
  ...rest
}: FormLeadtimeInputProps) => {
  const [value, setValue] = useState(resolveInitialValue(quantita, leadtime))

  const originalValueRef = useRef(resolveInitialValue(quantita, leadtime))

  const [loading, setLoading] = useState(false)
  const [status, setStatus] = useState<InputStatus>('default')
  const resetStatusTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearStatusTimeout = useCallback(() => {
    if (resetStatusTimeout.current) {
      clearTimeout(resetStatusTimeout.current)
      resetStatusTimeout.current = null
    }
  }, [])

  const scheduleStatusReset = useCallback(() => {
    clearStatusTimeout()
    resetStatusTimeout.current = setTimeout(() => {
      setStatus('default')
      resetStatusTimeout.current = null
    }, 2000)
  }, [clearStatusTimeout])

  useEffect(() => {
    return () => {
      clearStatusTimeout()
    }
  }, [clearStatusTimeout])

  useEffect(() => {
    const nextValue = resolveInitialValue(quantita, leadtime)
    setValue(nextValue)
    originalValueRef.current = nextValue
  }, [leadtime, quantita])

  const save = useCallback(async (nextValue: string) => {
    const originalValue = originalValueRef.current
    if (nextValue === originalValue)
      return
    const normalizedValue = normalizeValue(nextValue)
    if (typeof normalizedValue === 'undefined') {
      setStatus('error')
      scheduleStatusReset()
      setValue(originalValue)
      return
    }

    setStatus('default')
    setLoading(true)
    try {
      await onSave(normalizedValue)
      setStatus('success')
      originalValueRef.current = nextValue
      scheduleStatusReset()
    } catch (error) {
      console.error(error)
      setStatus('error')
      setValue(originalValue)
      scheduleStatusReset()
    } finally {
      setLoading(false)
    }
  }, [])

  const parsedValue = parseInt(value || '', 10)
  const variant = !parsedValue ? 'warning' : parsedValue >= 999 ? 'accent' : 'default'

  return (
    <FormBlurSaveInput
      loading={loading}
      status={status}
      variant={variant}
      disabled={disabled}
      className={className}
      {...rest}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={(e) => save(e.target.value)}
    />
  )
}

export default FormLeadtimeInput
